import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Redirect, NavLink } from "react-router-dom";
import { format } from 'date-fns';
import DateRangePicker from '@/components/ui/DateRangePicker';
import { userService } from '../../_services';
import { safeParseDate, cn } from '@/lib/utils';
import { computeMenuAlignFromTrigger } from '../../_helpers/dropdownViewportAlign';
import PagesWrapper from '@/components/ui/shared/PagesWrapper';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Search, X, SlidersHorizontal, ChevronDown } from 'lucide-react';
import BarListChart from '../BarListChart';
import MultiAxisBarChart from '../MultiAxisBarChart';
import ColorJson from '../../../src/whitelabel.json';

function Analytics({ AnalyticData }) {
    // Get default dates
    const getDefaultDates = useCallback(() => {
        const defaultStartDate = new Date();
        defaultStartDate.setDate(defaultStartDate.getDate() - 30);
        const defaultEndDate = new Date();
        
        const storedStartDate = localStorage.getItem('User_Start_Date');
        const storedEndDate = localStorage.getItem('User_End_Date');
        
        const startdate = (storedStartDate && storedStartDate !== "" && storedStartDate !== "null")
            ? safeParseDate(storedStartDate, defaultStartDate)
            : defaultStartDate;
        const enddate = (storedEndDate && storedEndDate !== "" && storedEndDate !== "null")
            ? safeParseDate(storedEndDate, defaultEndDate)
            : defaultEndDate;
            
        return {
            startDate: format(startdate, "MM/dd/yyyy"),
            endDate: format(enddate, "MM/dd/yyyy")
        };
    }, []);

    const [userdata] = useState(() => {
        try {
            return JSON.parse(localStorage.getItem('user'));
        } catch {
            return null;
        }
    });
    
    const defaultDates = useMemo(() => getDefaultDates(), [getDefaultDates]);
    const [startDate, setStartDate] = useState(defaultDates.startDate);
    const [endDate, setEndDate] = useState(defaultDates.endDate);
    const [graphData, setGraphData] = useState([]);
    const [searchDisplay, setSearchDisplay] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [filterTags, setFilterTags] = useState(null);
    const [filterMatchedTags, setFilterMatchedTags] = useState([]);
    const searchInputRef = useRef(null);
    const filterSlidersTriggerRef = useRef(null);
    const directOverrideTriggerRef = useRef(null);
    const [filterSlidersAlign, setFilterSlidersAlign] = useState('start');
    const [directOverrideAlign, setDirectOverrideAlign] = useState('start');
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [isEnrollEndpointEnabled, setIsEnrollEndpointEnabled] = useState(false);
    const [apiSignup, setApiSignup] = useState(false);
    const [enrollmentCenterSignup, setEnrollmentCenterSignup] = useState(false);
    const [clientAccessLinkSignup, setClientAccessLinkSignup] = useState(false);
    const [dist_usertype, setDist_usertype] = useState('Direct');
    const [userFilterList, setUserFilterList] = useState([]);
    const [enrollmentStatusLoaded, setEnrollmentStatusLoaded] = useState(false);
    // Claim Type tabs state
    const [ClaimType, setClaimType] = useState(() => {
        const storedType = localStorage.getItem('Updated_HD_Type') || 'totalpaid';
        return [
            { id: "totalpaid1", value: "totalpaid", isChecked: storedType === 'totalpaid', pname: "Total" },
            { id: "reversed3", value: "reversed", isChecked: storedType === 'reversed', pname: "Reversed" },
            { id: "netpaid2", value: "netpaid", isChecked: storedType === 'netpaid', pname: "Net" },
            { id: "compensable4", value: "compensable", isChecked: storedType === 'compensable', pname: "Paid" }
        ];
    });

    // Get primary and secondary colors based on domain
    const { primaryColor, secondaryColor } = useMemo(() => {
        const host = window.location.host;
        if (host.includes('rxhacker.com') || host.includes('localhost') || 
            host.includes('tempfilter.23byt.es') || host.includes('reporting.nulifespanrx.com')) {
            return {
                primaryColor: ColorJson.ApplicationColor.Rxhacker_primary_color,
                secondaryColor: ColorJson.ApplicationColor.Rxhacker_secondary_color
            };
        }
        return {
            primaryColor: ColorJson.ApplicationColor.Glic_primary_color,
            secondaryColor: ColorJson.ApplicationColor.Glic_secondary_color
        };
    }, []);

    const getTagTypeFullName = useCallback((stext) => {
        const tagMap = {
            'PBM': 'PCN',
            'DistributorName': 'Distributor Name',
            'PhCity': 'Pharmacy City',
            'PrCity': 'Prescriber City',
            'PhState': 'Pharmacy State',
            'PrState': 'Prescriber State',
            'PhZip': 'Pharmacy ZIP',
            'PrZip': 'Prescriber ZIP',
            'PharmacyName': 'Pharmacy Name',
            'GroupNumber': 'Group Number',
            'WholesalerName': 'Wholesaler Name',
            'DrugName': 'Drug Name',
            'Prescriber': 'Prescriber Name'
        };
        return tagMap[stext] || stext;
    }, []);

    const handleResponse = useCallback((response) => {
        return response.text().then(text => {
            const data = text && JSON.parse(text);
            return data;
        });
    }, []);

    const getWholesalersEnrollmentAPIStatus = useCallback((whId) => {
        if (!whId) {
            return;
        }
        const requestOptions = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ Wh_Id: whId })
        };
        
        fetch(`/api/Analytic/getWholesalers_Enrollment_API_Status`, requestOptions)
            .then(handleResponse)
            .then(apidata => {
                if (apidata && apidata.length > 0) {
                    setIsEnrollEndpointEnabled(apidata[0]?.colValue || false);
                    setApiSignup(apidata[1]?.colValue || false);
                    setEnrollmentCenterSignup(apidata[2]?.colValue || false);
                    setClientAccessLinkSignup(apidata[3]?.colValue || false);
                }
                setEnrollmentStatusLoaded(true);
            })
            .catch(error => {
                setEnrollmentStatusLoaded(true);
            });
    }, [handleResponse]);

    const bindFilteredTag = useCallback(async (userrole, subAdmin, wholesaler, wholesalerId, startDate, endDate, AdFilterJson) => {
        try {
            const response = await fetch('/api/Analytic/get_filtered_tag', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    user_type: userrole,
                    sub_admin_id: subAdmin,
                    wholesaler_name: wholesaler,
                    wholesaler_id: wholesalerId,
                    start_date: startDate,
                    end_date: endDate,
                    AdFilterJson: AdFilterJson
                })
            });
            const data = await response.json();
            if (data) {
                setFilterMatchedTags(data);
            }
        } catch (error) {

        }
    }, []);

    const changeData = useCallback((dateChanged, stdate, endate, ftype = '') => {
        const startDateObj = dateChanged ? stdate : startDate;
        const endDateObj = dateChanged ? endate : endDate;
        const AllDirectOverride = ftype === '' ? dist_usertype : ftype;
        
        const startDateFormatted = format(new Date(startDateObj), "yyyyMMdd");
        const endDateFormatted = format(new Date(endDateObj), "yyyyMMdd");
        const changedStartDate = format(new Date(startDateObj), "MM/dd/yyyy");
        const changedEndDate = format(new Date(endDateObj), "MM/dd/yyyy");
        
        setStartDate(changedStartDate);
        setEndDate(changedEndDate);
        
        const AdFilterJson = JSON.parse(localStorage.getItem('Analytic_Filter'));        
        let APIBaseURL = "";
        if (AllDirectOverride === 'All')
            APIBaseURL = '/api/BrokerAnalytic_All_Filter';
        else if (AllDirectOverride === 'Direct')
            APIBaseURL = '/api/BrokerAnalytic';
        else if (AllDirectOverride === 'Override')
            APIBaseURL = '/api/BrokerAnalytic_Override_Filter';
        else
            APIBaseURL = '/api/BrokerAnalytic';
        
        userService.GetWh_Analytic(userdata?.wholesaler_Id?.toString(), "").then(apidata => {
            if (apidata?.length > 0) {
                const filterTime = localStorage.getItem('Filter_Last_Update_Time');
                const rxsClaimType = localStorage.getItem('Updated_HD_Type');
                
                const Widget1 = JSON.stringify({
                    wname: String(userdata?.wholesaler_Id || ''),
                    start_date: startDateFormatted,
                    end_date: endDateFormatted,
                    rcount: 0,
                    chart_type: "columnList",
                    ispatientsaving: "true",
                    pharmacynumber: "all",
                    filtertime: filterTime,
                    rxsClaimType: rxsClaimType,
                    wholesaler_id: userdata?.wholesaler_Id,
                    widget_link: apidata[0].widgetAPI
                });
                
                const MultiAxis_DeviceSignup = JSON.stringify({
                    wname: String(userdata?.wholesaler_Id || ''),
                    start_date: startDateFormatted,
                    end_date: endDateFormatted,
                    rcount: 0,
                    chart_type: "column_multiaxislist",
                    ispatientsaving: "true",
                    pharmacynumber: "all",
                    filtertime: filterTime,
                    rxsClaimType: rxsClaimType,
                    wholesaler_id: userdata?.wholesaler_Id,
                    widget_link: ""
                });
                
                const Widget4 = JSON.stringify({
                    wname: String(userdata?.wholesaler_Id || ''),
                    start_date: startDateFormatted,
                    end_date: endDateFormatted,
                    rcount: 0,
                    chart_type: "columnList",
                    ispatientsaving: "true",
                    pharmacynumber: "all",
                    filtertime: filterTime,
                    rxsClaimType: rxsClaimType,
                    wholesaler_id: userdata?.wholesaler_Id,
                    widget_link: ""
                });
                
                let tempGraphData = [];
                
                // Build graph data based on enrollment status
                if (isEnrollEndpointEnabled) {
                    tempGraphData = [
                        {
                            url: APIBaseURL + "/date_wise_signup",
                            type: apidata[0].widgetType,
                            title: apidata[0].widgetTitle,
                            Subtitle: apidata[0].widgetSubTitle,
                            ColorCode: secondaryColor,
                            DataType: "NA",
                            XlabelString: "Date",
                            YlabelString: "Number of Signups",
                            interval: 'day',
                            id: "Alalytic_widget1",
                            RequestJson: Widget1,
                            RequestJson1: JSON.stringify(AdFilterJson)
                        },
                        {
                            url: APIBaseURL + "/gn_wise_signup",
                            type: apidata[1].widgetType,
                            title: apidata[1].widgetTitle,
                            Subtitle: apidata[1].widgetSubTitle,
                            ColorCode: secondaryColor,
                            DataType: "NA",
                            XlabelString: "Group Number",
                            YlabelString: "Number of Signups",
                            id: "Alalytic_widget2",
                            RequestJson: Widget1,
                            RequestJson1: JSON.stringify(AdFilterJson)
                        },
                        {
                            url: APIBaseURL + "/devicedatesignup",
                            type: "MultiBar_List",
                            title: "Client Access Link Signups",
                            Subtitle: "Number of signups through Client Access Links",
                            ColorCode: primaryColor,
                            DataType: "NA",
                            XlabelString: "Date",
                            YlabelString: "Number of Signups",
                            id: "Alalytic_widget3",
                            RequestJson: MultiAxis_DeviceSignup,
                            RequestJson1: JSON.stringify(AdFilterJson)
                        },
                        {
                            url: APIBaseURL + "/devicegnsignup",
                            type: "Bar_List",
                            title: "Client Access Link Signups by Group Number",
                            Subtitle: "Number of signups through Client Access Links for each group number",
                            ColorCode: secondaryColor,
                            DataType: "NA",
                            XlabelString: "Group Number",
                            YlabelString: "Number of Signups",
                            id: "Alalytic_widget4",
                            RequestJson: Widget4,
                            RequestJson1: JSON.stringify(AdFilterJson)
                        },
                        {
                            url: APIBaseURL + "/date_wise_signup_api",
                            type: "Bar_List",
                            title: "API Signups",
                            Subtitle: "Number of signups through API",
                            ColorCode: secondaryColor,
                            DataType: "NA",
                            XlabelString: "Date",
                            YlabelString: "Number of Signups",
                            interval: 'day',
                            id: "Alalytic_widget5",
                            RequestJson: Widget1,
                            RequestJson1: JSON.stringify(AdFilterJson)
                        },
                        {
                            url: APIBaseURL + "/gn_wise_signup_api",
                            type: "Bar_List",
                            title: "API Signups by Group Number",
                            Subtitle: "Number of signups through API for each group number",
                            ColorCode: secondaryColor,
                            DataType: "NA",
                            XlabelString: "Group Number",
                            YlabelString: "Number of Signups",
                            id: "Alalytic_widget6",
                            RequestJson: Widget1,
                            RequestJson1: JSON.stringify(AdFilterJson)
                        }
                    ];
                } else {
                    tempGraphData = [
                        {
                            url: APIBaseURL + "/date_wise_signup",
                            type: apidata[0].widgetType,
                            title: apidata[0].widgetTitle,
                            Subtitle: apidata[0].widgetSubTitle,
                            ColorCode: secondaryColor,
                            DataType: "NA",
                            XlabelString: "Date",
                            YlabelString: "Number of Signups",
                            interval: 'day',
                            id: "Alalytic_widget1",
                            RequestJson: Widget1,
                            RequestJson1: JSON.stringify(AdFilterJson)
                        },
                        {
                            url: APIBaseURL + "/gn_wise_signup",
                            type: apidata[1].widgetType,
                            title: apidata[1].widgetTitle,
                            Subtitle: apidata[1].widgetSubTitle,
                            ColorCode: secondaryColor,
                            DataType: "NA",
                            XlabelString: "Group Number",
                            YlabelString: "Number of Signups",
                            id: "Alalytic_widget2",
                            RequestJson: Widget1,
                            RequestJson1: JSON.stringify(AdFilterJson)
                        },
                        {
                            url: APIBaseURL + "/devicedatesignup",
                            type: "MultiBar_List",
                            title: "Client Access Link Signups",
                            Subtitle: "Number of signups through Client Access Links",
                            ColorCode: primaryColor,
                            DataType: "NA",
                            XlabelString: "Date",
                            YlabelString: "Number of Signups",
                            id: "Alalytic_widget3",
                            RequestJson: MultiAxis_DeviceSignup,
                            RequestJson1: JSON.stringify(AdFilterJson)
                        },
                        {
                            url: APIBaseURL + "/devicegnsignup",
                            type: "Bar_List",
                            title: "Client Access Link Signups by Group Number",
                            Subtitle: "Number of signups through Client Access Links for each group number",
                            ColorCode: secondaryColor,
                            DataType: "NA",
                            XlabelString: "Group Number",
                            YlabelString: "Number of Signups",
                            id: "Alalytic_widget4",
                            RequestJson: Widget4,
                            RequestJson1: JSON.stringify(AdFilterJson)
                        }
                    ];
                }
                
                // Filter based on signup types
                if (!enrollmentCenterSignup) {
                    tempGraphData = tempGraphData.filter(item => 
                        item.id !== "Alalytic_widget1" && item.id !== "Alalytic_widget2"
                    );
                }
                if (!clientAccessLinkSignup) {
                    tempGraphData = tempGraphData.filter(item => 
                        item.id !== "Alalytic_widget3" && item.id !== "Alalytic_widget4"
                    );
                }
                if (!apiSignup) {
                    tempGraphData = tempGraphData.filter(item => 
                        item.id !== "Alalytic_widget5" && item.id !== "Alalytic_widget6"
                    );
                }
                
                setGraphData(tempGraphData);
            }
        }).catch(error => {});
    }, [startDate, endDate, dist_usertype, userdata, primaryColor, secondaryColor, isEnrollEndpointEnabled, apiSignup, enrollmentCenterSignup, clientAccessLinkSignup]);

    const handleCalendar = useCallback((e, picker) => {
        const changedStartDate = format(safeParseDate(picker.startDate), "MM/dd/yyyy");
        const changedEndDate = format(safeParseDate(picker.endDate), "MM/dd/yyyy");
        localStorage.setItem('User_Start_Date', changedStartDate);
        localStorage.setItem('User_End_Date', changedEndDate);
        changeData(true, changedStartDate, changedEndDate);
        
        // Update filter tags
        const AnalyticFilterJson = JSON.parse(localStorage.getItem('Analytic_Filter'));
        if (AnalyticFilterJson) {
            setFilterTags(AnalyticFilterJson);
            const ds = format(new Date(changedStartDate), "yyyyMMdd");
            const de = format(new Date(changedEndDate), "yyyyMMdd");
            bindFilteredTag(
                userdata?.userrole, 
                -1, 
                userdata?.wholesaler, 
                userdata?.wholesaler_Id, 
                ds, 
                de, 
                JSON.stringify(AnalyticFilterJson)
            );
        }
    }, [changeData, userdata, bindFilteredTag]);

    const handlechangea = useCallback((e) => {
        if (e && e.heading && e.value) {
            // Validate heading is not undefined or invalid
            if (e.heading === 'undefined' || !e.heading) {
                return;
            }
            
            try {
                let Created_Advance_Filter = JSON.parse(localStorage.getItem('Advance_Filter')) || {};
                
                if (Created_Advance_Filter[e.heading]) {
                    if (!Created_Advance_Filter[e.heading].includes(e.value)) {
                        Created_Advance_Filter[e.heading].push(e.value);
                    }
                } else {
                    Created_Advance_Filter[e.heading] = [e.value];
                }
                
                localStorage.setItem('Advance_Filter', JSON.stringify(Created_Advance_Filter));
                localStorage.setItem('Filter_Last_Update_Time', new Date().toString());
                localStorage.setItem('Updated_AdFilter_Status', JSON.stringify(Created_Advance_Filter));
                
            } catch (error) {
                console.error('Error saving filter:', error);
            }
        }
        
        const startdate = format(safeParseDate(localStorage.getItem('User_Start_Date') || startDate), "MM/dd/yyyy");
        const enddate = format(safeParseDate(localStorage.getItem('User_End_Date') || endDate), "MM/dd/yyyy");
        localStorage.setItem('Filter_Last_Update_Time', new Date().toString());
        changeData(true, startdate, enddate);
    }, [startDate, endDate, changeData]);

    const searchBrokerData = useCallback(async (text) => {
        if (text.length < 2) {
            setSearchDisplay([]);
            setShowSearchResults(false);
            return;
        }
        
        try {
            const response = await fetch('/api/Filter/SearchOnTypeText_GetBrokerData', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ text, val: userdata?.wholesaler_Id })
            });
            const data = await response.json();
            setSearchDisplay(data || []);
            setShowSearchResults(true);
        } catch (error) {

            setSearchDisplay([]);
        }
    }, [userdata]);

    const handleSearchChange = useCallback((e) => {
        const value = e.target.value;
        setSearchValue(value);
        searchBrokerData(value);
    }, [searchBrokerData]);

    const handleSearchFocus = useCallback(() => {
        if (searchValue.length >= 2 && searchDisplay.length > 0) {
            setShowSearchResults(true);
        }
    }, [searchValue, searchDisplay]);

    const handleSearchItemClick = useCallback((item) => {
        setSearchValue('');
        setSearchDisplay([]);
        setShowSearchResults(false);
        
        // Create filter
        const nodetype = item.cType;
        const nodevalue = item.value;
        
        let Created_Advance_Filter = JSON.parse(localStorage.getItem('Analytic_Filter'));
        if (Created_Advance_Filter && typeof Created_Advance_Filter === 'object') {
            if (Created_Advance_Filter[nodetype]) {
                if (!Created_Advance_Filter[nodetype].includes(nodevalue)) {
                    Created_Advance_Filter[nodetype].push(nodevalue);
                }
            } else {
                Created_Advance_Filter[nodetype] = [nodevalue];
            }
        } else {
            Created_Advance_Filter = { [nodetype]: [nodevalue] };
        }
        
        localStorage.setItem('Analytic_Filter', JSON.stringify(Created_Advance_Filter));
        localStorage.setItem('An_Filter_Last_Update_Time', new Date().toString());
        setFilterTags(Created_Advance_Filter);
        
        const startdate = format(safeParseDate(localStorage.getItem('User_Start_Date') || startDate), "MM/dd/yyyy");
        const enddate = format(safeParseDate(localStorage.getItem('User_End_Date') || endDate), "MM/dd/yyyy");
        changeData(true, startdate, enddate);
        
        // Update filtered tags
        const ds = format(new Date(startdate), "yyyyMMdd");
        const de = format(new Date(enddate), "yyyyMMdd");
        bindFilteredTag(
            userdata?.userrole, 
            -1, 
            userdata?.wholesaler, 
            userdata?.wholesaler_Id, 
            ds, 
            de, 
            JSON.stringify(Created_Advance_Filter)
        );
    }, [startDate, endDate, changeData, userdata, bindFilteredTag]);

    const handleRemoveTag = useCallback((nodename, nodevalue) => {
        try {
            let ExistJson = JSON.parse(localStorage.getItem('Analytic_Filter'));
            if (ExistJson && ExistJson[nodename]) {
                const ItemIndex = ExistJson[nodename].indexOf(nodevalue);
                if (ItemIndex > -1) {
                    ExistJson[nodename].splice(ItemIndex, 1);
                    if (ExistJson[nodename].length === 0) {
                        delete ExistJson[nodename];
                    }
                }
                
                if (Object.keys(ExistJson).length === 0) {
                    localStorage.setItem('Analytic_Filter', null);
                    setFilterTags(null);
                    setFilterMatchedTags([]);
                } else {
                    localStorage.setItem('Analytic_Filter', JSON.stringify(ExistJson));
                    setFilterTags(ExistJson);
                }
                
                handlechangea();
            }
        } catch (error) {

        }
    }, [handlechangea]);

    const handleResetButton = useCallback(() => {
        localStorage.setItem('Analytic_Filter', null);
        setFilterTags(null);
        setFilterMatchedTags([]);
        handlechangea();
    }, [handlechangea]);

    // Fetch enrollment status on mount
    useEffect(() => {
        if (userdata?.wholesaler_Id) {
            getWholesalersEnrollmentAPIStatus(userdata.wholesaler_Id);
        }
    }, [userdata, getWholesalersEnrollmentAPIStatus]);

    // Initialize filter tags on mount
    useEffect(() => {
        try {
            const storedFilter = JSON.parse(localStorage.getItem('Analytic_Filter'));
            setFilterTags(storedFilter);
            
            if (storedFilter && userdata) {
                const ds = format(new Date(startDate), "yyyyMMdd");
                const de = format(new Date(endDate), "yyyyMMdd");
                bindFilteredTag(
                    userdata.userrole, 
                    -1, 
                    userdata.wholesaler, 
                    userdata.wholesaler_Id, 
                    ds, 
                    de, 
                    JSON.stringify(storedFilter)
                );
            }
        } catch {
            setFilterTags(null);
        }
    }, [userdata, startDate, endDate, bindFilteredTag]);

    // Load data after enrollment status is fetched
    useEffect(() => {
        // Only proceed if enrollment status has been loaded
        if (!enrollmentStatusLoaded || !userdata?.wholesaler_Id) {
            return;
        }
        
        // Load data regardless of enrollment status values
        changeData();
    }, [enrollmentStatusLoaded, userdata, changeData]);

    // Handle Claim Type tab click (Total/Reversed/Net/Paid)
    const HandleRXSRadioButton = useCallback((event) => {
        const selectedValue = event.target.value;
        
        // Update ClaimType state - set the clicked one as checked
        setClaimType(prev => prev.map(item => ({
            ...item,
            isChecked: item.value === selectedValue
        })));
        
        // Update localStorage
        localStorage.setItem('Updated_HD_Type', selectedValue);
        localStorage.setItem('Filter_Last_Update_Time', new Date().toISOString());
        
        // Handle PBM: HD filter based on claim type
        if (selectedValue !== 'totalpaid') {
            // Add PBM: HD filter for non-totalpaid claim types
            try {
                let advanceFilter = JSON.parse(localStorage.getItem('Analytic_Filter'));
                if (!advanceFilter) advanceFilter = {};
                if (!advanceFilter.PBM) advanceFilter.PBM = [];
                if (!advanceFilter.PBM.includes('HD')) {
                    advanceFilter.PBM.push('HD');
                }
                localStorage.setItem('Analytic_Filter', JSON.stringify(advanceFilter));
            } catch (error) {
                localStorage.setItem('Analytic_Filter', '{"PBM":["HD"]}');
            }
        } else {
            // Remove PBM: HD filter when switching to Total (totalpaid)
            try {
                let advanceFilter = JSON.parse(localStorage.getItem('Analytic_Filter'));
                if (advanceFilter && advanceFilter.PBM) {
                    const hdIndex = advanceFilter.PBM.indexOf('HD');
                    if (hdIndex > -1) {
                        advanceFilter.PBM.splice(hdIndex, 1);
                        if (advanceFilter.PBM.length === 0) {
                            delete advanceFilter.PBM;
                        }
                        localStorage.setItem('Analytic_Filter', Object.keys(advanceFilter).length === 0 ? null : JSON.stringify(advanceFilter));
                    }
                }
            } catch (error) {
                // Silent fail
            }
        }
        
        // Trigger data reload
        changeData();
    }, [changeData]);

    const applyUserFilter = useCallback((item) => {
        try {
            let parsedFilter = null;
            try {
                parsedFilter = JSON.parse(item.filterJson);
            } catch (e) {
                parsedFilter = item.filterJson;
            }
            
            localStorage.setItem('Analytic_Filter', JSON.stringify(parsedFilter));
            
            // Trigger data reload
            changeData();
        } catch (error) {
            console.error('Error applying filter:', error);
        }
    }, [changeData]);

    const loadUserFilters = useCallback(() => {
        if (!userdata) return;
        try {
            userService.Get_FilterName_List(userdata.paramsname, userdata.wholesaler_Id, 'Analytics').then(
                apidata => {
                    if (apidata && Array.isArray(apidata)) {
                        setUserFilterList(apidata);
                    }
                }
            );
        } catch (error) {
            console.error('Error loading user filters:', error);
        }
    }, [userdata]);

    const handleFilterTypeChange = useCallback((filterType) => {
        setDist_usertype(filterType);
        changeData(undefined, undefined, undefined, filterType);
    }, [changeData]);

    // Load user filters on mount
    useEffect(() => {
        loadUserFilters();
    }, [loadUserFilters]);


    // Auth checks
    const userType = userService.Get_User_Type();
    if (userService.Check_LoggedIn_Status() === 'Login') {
        return <Redirect to="/login" />;
    }
    if (['admin', 'distributor', 'subadmin'].includes(userType?.toLowerCase())) {
        return <Redirect to='/pageNotFound' />;
    }

    return (
        <PagesWrapper>
            <div className="flex flex-col gap-4">
                <div className="flex items-center">
                    <h1 className="text-2xl font-semibold text-foreground">
                        Signups
                    </h1>
                </div>

                <Card>
                    <CardContent className="analytics-card-content">
                        <div className="flex flex-wrap items-end gap-4">
                            <DateRangePicker
                                className="w-[320px] max-w-full flex-shrink-0 h-10"
                                initialSettings={{
                                    startDate: startDate,
                                    endDate: endDate,
                                    maxDate: new Date(),
                                    locale: {
                                        format: 'MM/DD/YYYY'
                                    }
                                }}
                                onApply={handleCalendar}>
                                <div className="flex items-center gap-0 w-[320px] max-w-full rounded-md overflow-hidden border border-input bg-card hover:shadow-sm transition-shadow h-10">
                                    <div className="flex items-center gap-1 px-3 flex-1">
                                        <span className="text-sm text-muted-foreground whitespace-nowrap">From</span>
                                        <Input
                                            type="text"
                                            className="bg-transparent text-sm cursor-pointer w-full !border-0 outline-none h-auto p-0 rounded-none"
                                            value={startDate}
                                            readOnly
                                        />
                                    </div>
                                    <div className="h-6 w-px bg-border flex-shrink-0" />
                                    <div className="flex items-center gap-1 px-3 flex-1">
                                        <span className="text-sm text-muted-foreground whitespace-nowrap">To</span>
                                        <Input
                                            type="text"
                                            className="bg-transparent text-sm cursor-pointer w-full !border-0 outline-none h-auto p-0 rounded-none"
                                            value={endDate}
                                            readOnly
                                        />
                                    </div>
                                </div>
                            </DateRangePicker>
                            
                            {/* Search Input - Takes remaining width */}
                            <div className="relative flex-1 min-w-[200px]">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                                <Input
                                    ref={searchInputRef}
                                    type="text"
                                    placeholder="Search by pharmacy, drug, or group..."
                                    className="searchFilter pl-9"
                                    value={searchValue}
                                    onChange={handleSearchChange}
                                    onFocus={handleSearchFocus}
                                    onBlur={() => setTimeout(() => setShowSearchResults(false), 200)}
                                />
                                {showSearchResults && searchDisplay.length > 0 && (
                                    <div className="absolute top-full left-0 right-0 mt-1 w-full bg-popover border rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                                        {searchDisplay.map((item, index) => (
                                            <div
                                                key={index}
                                                className={`px-3 py-2 hover:bg-accent cursor-pointer text-sm ${index < searchDisplay.length - 1 ? 'border-b' : ''}`}
                                                onClick={() => handleSearchItemClick(item)}
                                            >
                                                {item.value}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Filter Icon Dropdown */}
                            <DropdownMenu onOpenChange={(open) => { if (open) setFilterSlidersAlign(computeMenuAlignFromTrigger(filterSlidersTriggerRef.current)); }}>
                                <DropdownMenuTrigger asChild>
                                    <Button ref={filterSlidersTriggerRef} variant="outline" size="icon">
                                        <SlidersHorizontal className="h-4 w-4" />
                                    </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align={filterSlidersAlign} className="w-48">
                                    <DropdownMenuItem asChild>
                                        <NavLink to="/filter">Create New Filter</NavLink>
                                    </DropdownMenuItem>
                                    {userFilterList != null && userFilterList.length > 0 && (
                                        <>
                                            <DropdownMenuSeparator />
                                            {userFilterList.map((e1) => (
                                                <DropdownMenuItem key={e1.dashboardId} onClick={() => applyUserFilter(e1)}>
                                                    {e1.filterName}
                                                </DropdownMenuItem>
                                            ))}
                                        </>
                                    )}
                                </DropdownMenuContent>
                            </DropdownMenu>

                            {/* Direct/Override Dropdown */}
                            {userdata?.overrideCount > 0 && (
                                <DropdownMenu onOpenChange={(open) => { if (open) setDirectOverrideAlign(computeMenuAlignFromTrigger(directOverrideTriggerRef.current)); }}>
                                    <DropdownMenuTrigger asChild>
                                        <Button ref={directOverrideTriggerRef} variant="secondary" className="h-10 gap-2 flex-shrink-0">
                                            {dist_usertype || 'Direct'}<ChevronDown className="h-4 w-4" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align={directOverrideAlign} className="w-40 p-2">
                                        <DropdownMenuItem 
                                            onClick={() => handleFilterTypeChange('All')}
                                            className="text-left px-3 py-2 text-sm font-medium hover:bg-accent transition-colors cursor-pointer border-b rounded-none"
                                        >
                                            All
                                        </DropdownMenuItem>
                                        <DropdownMenuItem 
                                            onClick={() => handleFilterTypeChange('Direct')}
                                            className="text-left px-3 py-2 text-sm font-medium hover:bg-accent transition-colors cursor-pointer border-b rounded-none"
                                        >
                                            Direct
                                        </DropdownMenuItem>
                                        <DropdownMenuItem 
                                            onClick={() => handleFilterTypeChange('Override')}
                                            className="text-left px-3 py-2 text-sm font-medium hover:bg-accent transition-colors cursor-pointer rounded-none"
                                        >
                                            Override
                                        </DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}

                            {/* Claim Type Tabs - Only for legacy users */}
                            {(userdata?.isBrokerTypeUser === false && userdata?.isLegacyUser === true) && (
                                <div className="flex rounded-lg bg-muted p-1 flex-shrink-0">
                                    {ClaimType.map((p, index) => (
                                        <React.Fragment key={index}>
                                            <Button 
                                                id={p.id} 
                                                onClick={HandleRXSRadioButton} 
                                                value={p.value} 
                                                variant={p.isChecked ? "default" : "ghost"}
                                                className={cn(
                                                    "px-3 py-2 text-sm font-medium rounded-md whitespace-nowrap cursor-pointer",
                                                    !p.isChecked && "text-muted-foreground hover:text-foreground hover:bg-background"
                                                )}
                                            >
                                                {p.pname}
                                            </Button>
                                            {!p.isChecked && index < ClaimType.length - 1 && (
                                                <div className="w-px bg-border self-stretch my-2" />
                                            )}
                                        </React.Fragment>
                                    ))}
                                </div>
                            )}
                        </div>
                    </CardContent>
                </Card>

                {/* Filter Tags - Outside Card */}
                {filterTags && Object.keys(filterTags).length > 0 && (
                    <div className="flex items-center gap-4">
                        {/* Scrollable tags container */}
                        <div className="overflow-x-auto">
                            <div className="flex items-center gap-4 pb-2 min-w-min">
                        {Object.keys(filterTags).map((key) =>
                            filterTags[key].map((value, index) => {
                                const matchedTag = filterMatchedTags.find(
                                    tag => tag.tableType === key && tag.value === value
                                );
                                const hasData = matchedTag?.hasData === "True";
                                
                                return (
                                    <Badge
                                        key={`${key}-${index}`}
                                        variant="outline"
                                        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-normal leading-none whitespace-nowrap bg-primary/10 text-primary border-primary"
                                    >
                                        {getTagTypeFullName(key)}: {value}
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            onClick={() => handleRemoveTag(key, value)}
                                            className="h-4 w-4 p-0 hover:bg-transparent hover:text-current [&_svg]:size-2.5"
                                        >
                                            <X />
                                        </Button>
                                    </Badge>
                                );
                            })
                        )}
                            </div>
                        </div>
                        {/* Reset button - fixed outside scroll */}
                        <Button
                            variant="ghost" className="h-auto py-1 px-3 gap-1.5 whitespace-nowrap flex-shrink-0 leading-[1.15] bg-foreground text-card border border-foreground hover:bg-foreground hover:text-card hover:opacity-85"
                            onClick={handleResetButton}
                        >
                            Reset
                        </Button>
                    </div>
                )}

                <div className="grid grid-cols-1 2xl:grid-cols-2 gap-3">
                    {graphData.map((item, index) => (
                        <div key={`graph-${item.id}-${index}`} className="min-h-[400px]">
                            {item.type === "Bar_List" && (
                                <BarListChart
                                    data={item}
                                    _Index={index}
                                    handlechangea={handlechangea}
                                />
                            )}
                            {item.type === "MultiBar_List" && (
                                <MultiAxisBarChart
                                    data={item}
                                    _Index={index}
                                    handlechangea={handlechangea}
                                />
                            )}
                                    </div>
                                ))}
                            </div>
            </div>
        </PagesWrapper>
    );
}

export default Analytics;
